import { type ReactElement } from 'react';
import {
  buildTableV2PaginationTokens,
  tableV2FooterHeight,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { r, s } from '@/theme/tokens';
import { Box, IconButton, MenuItem, Select, Text } from '@/ui/primitives';
import { ChevronRightIcon, KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import type { SxProps, Theme } from '@/ui/primitives';

export const TableV2Footer = ({
  label,
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  contentInsetX = s('lg'),
  showControls = true,
  onPageChange,
  onPageSizeChange,
}: {
  label: string;
  page: number;
  pageCount: number;
  pageSize: number;
  pageSizeOptions: number[];
  contentInsetX?: string;
  showControls?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}): ReactElement => {
  const paginationTokens = buildTableV2PaginationTokens(page, pageCount);
  const previousPageDisabled = page <= 0;
  const nextPageDisabled = page >= pageCount - 1;
  const paginationChevronButtonSx: SxProps<Theme> = {
    height: s('3xl'),
    width: s('3xl'),
    '& > span': {
      height: (theme: Theme) => theme.app.iconSizes.xl,
      width: (theme: Theme) => theme.app.iconSizes.xl,
    },
  };
  const paginationTokenSlotSx: SxProps<Theme> = {
    alignItems: 'center',
    display: 'inline-flex',
    flex: '0 0 auto',
    height: s('2xl'),
    justifyContent: 'center',
    width: s('2xl'),
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'background.paper',
        borderTop: (theme) => `1px solid ${theme.app.colors.border}`,
        display: 'flex',
        height: tableV2FooterHeight,
        justifyContent: 'space-between',
        minWidth: 0,
        pl: contentInsetX,
      }}
    >
      <Text role="body" color="textSecondary" sx={{ px: s('md') }}>
        {label}
      </Text>
      {showControls ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: s('md'), pr: contentInsetX }}>
          <Select<number>
            className="flowqa-table-v2-page-size-select"
            fullWidth={false}
            IconComponent={KeyboardArrowDownIcon}
            inputProps={{ 'aria-label': 'Rows per page' }}
            SelectDisplayProps={{
              'aria-label': 'Rows per page',
              style: {
                alignItems: 'center',
                display: 'flex',
                height: 40,
                minHeight: 40,
                paddingBottom: 0,
                paddingTop: 0,
              },
            }}
            inputHeight={40}
            value={pageSize}
            onChange={(event) => {
              onPageSizeChange(event.target.value);
            }}
            sx={{
              minWidth: `calc(${s('3xl')} * 3)`,
              '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
                borderRadius: r('md'),
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.app.colors.border,
                borderRadius: r('md'),
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.app.colors.primary,
                borderWidth: '2px',
              },
              '& .MuiSelect-select': {
                alignItems: 'center',
                display: 'flex',
                fontSize: '15px',
                height: '40px !important',
                lineHeight: 1.4,
                minHeight: '40px !important',
                pl: `${s('lg')} !important`,
                py: `${s('none')} !important`,
                pr: `${s('3xl')} !important`,
              },
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
                right: s('md'),
              },
            }}
          >
            {pageSizeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {String(option)} per page
              </MenuItem>
            ))}
          </Select>
          <IconButton
            aria-label="Go to previous page"
            disabled={previousPageDisabled}
            size="large"
            sx={paginationChevronButtonSx}
            onClick={() => {
              onPageChange(page - 1);
            }}
          >
            <ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
          {paginationTokens.map((token, index) =>
            token === 'ellipsis' ? (
              <Text
                key={`table-v2-pagination-ellipsis-${String(index)}`}
                role="body"
                component="span"
                color="textSecondary"
                sx={paginationTokenSlotSx}
              >
                ...
              </Text>
            ) : (
              <Box
                key={`table-v2-pagination-page-${String(token)}`}
                aria-current={token === page ? 'page' : undefined}
                aria-label={`Go to page ${String(token + 1)}`}
                component="button"
                type="button"
                onClick={() => {
                  onPageChange(token);
                }}
                sx={[
                  paginationTokenSlotSx,
                  {
                    backgroundColor: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: r('pill'),
                    color: 'text.primary',
                    cursor: token === page ? 'default' : 'pointer',
                    font: 'inherit',
                    p: s('none'),
                    position: 'relative',
                    '&::after':
                      token === page
                        ? {
                            border: (theme: Theme) => `1px solid ${theme.app.colors.textMuted}`,
                            borderRadius: r('pill'),
                            content: '""',
                            inset: `-${s('sm')}`,
                            pointerEvents: 'none',
                            position: 'absolute',
                          }
                        : undefined,
                    '&:hover': {
                      backgroundColor: (theme) =>
                        token === page ? 'transparent' : theme.app.state.hover,
                    },
                  },
                ]}
              >
                <Text role="body" component="span" color="inherit">
                  {token + 1}
                </Text>
              </Box>
            ),
          )}
          <IconButton
            aria-label="Go to next page"
            disabled={nextPageDisabled}
            size="large"
            sx={paginationChevronButtonSx}
            onClick={() => {
              onPageChange(page + 1);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  );
};
